import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { getApiUrl } from '../config/api';

// Estado inicial
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true
};

// Actions
const authActions = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  TOKEN_VERIFIED: 'TOKEN_VERIFIED'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case authActions.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false
      };
    
    case authActions.TOKEN_VERIFIED:
      const token = localStorage.getItem('authToken');
      return {
        ...state,
        user: action.payload.user,
        token: token,
        isAuthenticated: true,
        isLoading: false
      };
    
    case authActions.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      };
    
    case authActions.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    default:
      return state;
  }
}

// Context
const AuthContext = createContext();

// Provider
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Debug - monitorar mudanças importantes no estado
  useEffect(() => {
    if (!state.isLoading && state.isAuthenticated) {
      console.log('✅ Usuário autenticado:', state.user?.name);
    }
  }, [state.isAuthenticated, state.isLoading, state.user]);

  // Função de login
  const login = React.useCallback(async (email, password) => {
    try {
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || data.error);
      }

      // Salvar no localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Atualizar estado
      dispatch({
        type: authActions.LOGIN_SUCCESS,
        payload: {
          user: data.user,
          token: data.token
        }
      });

      return { success: true };

    } catch (error) {
      throw new Error(error.message || 'Erro ao fazer login');
    }
  }, []);

  // Função de logout
  const logout = React.useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    dispatch({ type: authActions.LOGOUT });
  }, []);

  // Verificar token ao carregar a aplicação
  const verifyToken = React.useCallback(async () => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      dispatch({ type: authActions.SET_LOADING, payload: false });
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/auth/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        dispatch({
          type: authActions.TOKEN_VERIFIED,
          payload: {
            user: data.user
          }
        });
      } else {
        // Token inválido, fazer logout
        logout();
      }

    } catch (error) {
      console.error('Erro ao verificar token:', error);
      logout();
    } finally {
      dispatch({ type: authActions.SET_LOADING, payload: false });
    }
  }, [logout]);

  // Verificar token ao montar o componente
  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        dispatch({ type: authActions.SET_LOADING, payload: false });
        return;
      }

      // Carregar dados do localStorage primeiro (mais rápido)
      try {
        const user = JSON.parse(userData);
        
        dispatch({
          type: authActions.LOGIN_SUCCESS,
          payload: {
            user: user,
            token: token
          }
        });
        
      } catch (e) {
        console.error('Erro ao parsear dados do localStorage:', e);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        dispatch({ type: authActions.SET_LOADING, payload: false });
        return;
      }

      // Verificar com a API em background
      try {
        console.log('🔍 Verificando token...');
        const response = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        console.log('📊 Resposta da verificação:', data);

        if (!data.success) {
          console.log('❌ Token inválido, fazendo logout');
          logout();
        } else {
          console.log('✅ Token válido mantido');
        }

      } catch (error) {
        console.error('❌ Erro na verificação do token:', error);
      } finally {
        dispatch({ type: authActions.SET_LOADING, payload: false });
      }
    };

    checkToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoizar o value para evitar re-renders desnecessários
  const value = React.useMemo(() => ({
    ...state,
    login,
    logout,
    verifyToken
  }), [state, login, logout, verifyToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook para usar o contexto
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

// Exportar o contexto também
export { AuthContext };